import React, { useCallback, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSupplierOrderStore } from "../../store/useSupplierOrderStore.ts";
import { getSupplierOrders } from "../../services/supplierOrderService";

import SupplierOrderCard from "../../components/Fullfilment/SupplierOrderCard";
import SupplierOrderFilters from "../../components/Fullfilment/SupplierOrderFilters";
import Pagination from "../../components/Pagination";
import SupplierOrderTabs from "../../components/Fullfilment/SupplierOrderTabs";
import SupplierSearchBox from "../../components/Fullfilment/SupplierSearchBox";
import SupplierReviewViewModal from "../../components/Fullfilment/Rating/supplier/SupplierReviewViewModal";

const SupplierOrder = () => {
  const {
    activeTab,
    page,
    setPage,
    pageSize,
    setFilter,
    getFilters,

    isReviewViewModalOpen,
    reviewEntity,
    reviewData,
    closeReviewModal,
  } = useSupplierOrderStore();

  const filters = getFilters();

  const handleSearch = useCallback(
    (value) => {
      setFilter("search", value);
    },
    [setFilter]
  );

  const queryKey = useMemo(
    () => [
      "supplier-orders",
      activeTab,
      page,
      pageSize,
      filters.fulfillmentStatus,
      filters.dateFrom,
      filters.dateTo,
      filters.minAmount,
      filters.maxAmount,
      filters.shipmentStatus,
      filters.deliveryMethod,
      filters.search,
    ],
    [
      activeTab,
      page,
      pageSize,
      filters.fulfillmentStatus,
      filters.dateFrom,
      filters.dateTo,
      filters.minAmount,
      filters.maxAmount,
      filters.shipmentStatus,
      filters.deliveryMethod,
      filters.search,
    ]
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      getSupplierOrders({
        tab: activeTab,
        page,
        pageSize,
        ...filters,
      }),
    staleTime: 1000 * 60,

    // اختیاری ولی پیشنهاد می‌شود:
    // هنگام تغییر صفحه، داده قبلی را تا آمدن داده جدید نگه می‌دارد.
    // اگر @tanstack/react-query v5 داری:
    placeholderData: keepPreviousData,
  });

  const ordersList = Array.isArray(data) ? data : data?.results || [];

  const totalCount =
    typeof data?.count === "number"
      ? data.count
      : Array.isArray(data)
        ? data.length
        : 0;

  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0;

  let content;

  if (isLoading) {
    content = <SkeletonLoader count={5} />;
  } else if (error) {
    content = (
      <div className="text-center text-red-600 p-8 bg-white rounded-lg shadow">
        <p>خطا در دریافت سفارش‌ها: {error.message}</p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
        >
          تلاش مجدد
        </button>
      </div>
    );
  } else if (ordersList.length === 0) {
    content = (
      <div className="text-center text-gray-500 p-12 bg-white rounded-lg shadow border-2 border-dashed">
        <p className="text-lg">هیچ سفارشی با این مشخصات یافت نشد.</p>
      </div>
    );
  } else {
    content = (
      <div className="space-y-4">
        {ordersList.map((order) => (
          <SupplierOrderCard key={order.id} order={order} />
        ))}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <header className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900">
          پنل مدیریت سفارشات
        </h1>

        <div className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full">
          تعداد کل: {totalCount} مورد
        </div>
      </header>

      <SupplierOrderTabs />

      <SupplierSearchBox onSearch={handleSearch} />

      <SupplierOrderFilters />

      {isFetching && !isLoading && (
        <div className="mb-3 text-sm text-gray-500">
          در حال بروزرسانی اطلاعات...
        </div>
      )}

      <div className="mt-6">{content}</div>

      {totalPages > 1 && (
        <div className="mt-8">
          <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        </div>
      )}

      <SupplierReviewViewModal
        open={isReviewViewModalOpen}
        onClose={closeReviewModal}
        entity={reviewEntity}
        reviewData={reviewData}
      />
    </div>
  );
};

const SkeletonLoader = ({ count = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse bg-white p-6 rounded-xl shadow-sm border border-gray-100"
      >
        <div className="flex justify-between mb-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-6 bg-gray-200 rounded w-1/6"></div>
        </div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-100 rounded w-full"></div>
          <div className="h-4 bg-gray-100 rounded w-5/6"></div>
        </div>
      </div>
    ))}
  </div>
);

export default SupplierOrder;
